import { ApolloError, gql } from '@apollo/client'
import * as R from 'ramda'
import { useCallback, useMemo } from 'react'
import {
  BillCheckoutInput,
  DepositCheckoutInput,
  DocumentPaymentDetailInput,
  DocumentType,
  FiscalType,
  useCreateCoDocumentMutation,
  useFinalizeCoDocumentMutation,
  useUpdateCoDocumentMutation,
  useDeleteCoDocumentMutation,
  useCoBillsAndDepositsQuery,
  CoBillFragment,
  CoDepositFragment,
  CoProductionAssignedFragment,
  CoDepositAssignedFragment,
  useCoDocumentQuery,
  CoDocumentFragment,
  CoReservationFragment,
  useUndoCoDocumentMutation,
  useCreateCreditNoteMutation,
  useCreateManualDocumentMutation,
  ManualDocumentInput,
  useCreateCreditNoteForAdvanceMutation,
  DocumentDetailInput,
  useUpdateFinalizedDocumentMutation,
  useCashDesksQuery,
  CheckoutDocumentGroupType,
  DocumentInput,
  useCoReservationQuery,
  useUpdateDocumentPaymentDetailsMutation,
} from 'src/api/api.generated'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import syncMutation from 'src/utils/sync-mutation'
import { input } from 'zod'

//#region QUERY

export type CreateCoDocumentData =
  | {
      bills: CoBillFragment[]
      deposits: CoDepositFragment[]
      reservations: CoReservationFragment[]
    }
  | undefined

export function useCreateCoDocumentQuery(
  propertyId: string, //
  billIds: string[],
  productionAssignedIds: string[],
  depositIds: string[],
  skip: boolean,
) {
  const { id: tenantId } = useTenant()
  const {
    data: queryData,
    loading,
    error,
  } = useCoBillsAndDepositsQuery({
    variables: {
      tenantId,
      propertyId,
      billIds, //FIXME: invece di passare tutti i conti si potrebbero filtrare in base agli addebiti selezionati.
      depositIds,
    },
    skip,
  })

  const data: CreateCoDocumentData = useMemo(() => {
    if (!queryData) {
      return queryData
    }

    const checkProductions = productionAssignedIds.length > 0

    const bills: CoBillFragment[] = (queryData.bills || []).reduce((prev: CoBillFragment[], current: CoBillFragment) => {
      const productions = (checkProductions ? current.productions?.filter((pa) => productionAssignedIds.includes(pa.id)) : current.productions) || []
      if (productions.length <= 0) {
        return prev
      }
      const bill = { ...current, productions }
      return [...prev, bill]
    }, [])

    if (bills.length <= 0) {
      return { bills: [], deposits: [], reservations: [] }
    }

    const productionAssigneds = bills.reduce((prev: CoProductionAssignedFragment[], current: CoBillFragment) => {
      return [...prev, ...(current.productions || [])]
    }, [])

    const listOfBillId = bills.map((b) => b.id)
    const listOfReservId = bills.flatMap((b) => [...(b.reservation?.map((r) => r.id) ?? [])])
    const listOfReservResId = bills.flatMap((b) => [...(b.reservedResourceId ?? [])])

    // const listOfReservResId: string[] =
    //   bills.reduce((acc: string[], curr) => {
    //     const result = curr.reservedResourceId ? curr.reservedResourceId : []
    //     return [...acc, ...result]
    //   }, []) ?? []

    const deposits: CoDepositFragment[] = (queryData.documents || []) //
      .filter((d) => d.documentType === DocumentType.ADVANCE)
      .filter((d) => d.depositAssigneds && d.depositAssigneds.length > 0)
      .reduce((prevDeposits: CoDepositFragment[], currentDeposit: CoDepositFragment) => {
        const depositAssigneds = (currentDeposit.depositAssigneds || [])
          .filter((da) => (da.documentId ? false : true))
          .reduce((prevDepositAssigneds: CoDepositAssignedFragment[], currentDepositAssigned: CoDepositAssignedFragment) => {
            // let relatedPas: CoProductionAssignedFragment[] = []

            // if (currentDepositAssigned.billId) {
            //   relatedPas = productionAssigneds.filter((pa) => pa.bill?.id === currentDepositAssigned.billId)
            // }
            // if (currentDepositAssigned.reservedResourceId && relatedPas.length === 0) {
            //   relatedPas = productionAssigneds.filter((pa) => pa.production.reservedResourceId === currentDepositAssigned.reservedResourceId)
            // }
            // const reservationId = currentDepositAssigned.reservationId
            // if (reservationId && relatedPas.length === 0) {
            //   relatedPas = productionAssigneds.filter((pa) => pa.bill.reservationId && pa.bill.reservationId.includes(reservationId))
            // }

            const amount = currentDepositAssigned.amount
            if (amount > 0) {
              if (
                (currentDepositAssigned.reservedResourceId && listOfReservResId.includes(currentDepositAssigned.reservedResourceId)) ||
                (currentDepositAssigned.reservationId && listOfReservId.includes(currentDepositAssigned.reservationId)) ||
                (currentDepositAssigned.billId && listOfBillId.includes(currentDepositAssigned.billId))
              )
                return [...prevDepositAssigneds, { ...currentDepositAssigned, amount }]
            }
            return prevDepositAssigneds
          }, [])

        if (depositAssigneds.length > 0) {
          return [...prevDeposits, { ...currentDeposit, depositAssigneds }]
        } else {
          return prevDeposits
        }
      }, [])

    const reservations = R.uniqBy(
      (r) => r.id,
      bills.reduce((prev: CoReservationFragment[], current) => [...prev, ...(current.reservation || []).reduce((p: CoReservationFragment[], c) => (c ? [...p, c] : p), [])], []),
    )

    return { bills, deposits, reservations }
  }, [queryData])

  return { data, loading, error }
}

type CreateCoReservationData =
  | {
      reservations: CoReservationFragment[]
    }
  | undefined
export function useReservationCoDocumentQuery({ propertyId, reservationId, skip }: { propertyId: string; reservationId: string; skip: boolean }) {
  const { id: tenantId } = useTenant()
  const {
    data: queryData,
    loading,
    error,
  } = useCoReservationQuery({
    variables: {
      tenantId,
      propertyId,
      id: reservationId,
    },
    skip,
  })

  const data: CreateCoReservationData = useMemo(() => {
    if (!queryData || !queryData.reservations?.[0]) {
      return undefined
    }

    return {
      reservations: [queryData.reservations[0]],
    }
  }, [queryData])

  return { data, loading, error }
}

export type UpdateCoDocumentData = { document: CoDocumentFragment | null; reservations: CoReservationFragment[] } | undefined
export function useUpdateCoDocumentQuery(propertyId: string, documentId: string, skip: boolean) {
  const { id: tenantId } = useTenant()
  const {
    data: queryData,
    loading,
    error,
  } = useCoDocumentQuery({
    variables: {
      propertyId,
      tenantId,
      documentId,
    },
    skip,
    fetchPolicy: 'no-cache',
  })

  const data: UpdateCoDocumentData = useMemo(() => {
    if (!queryData) {
      return queryData
    }

    const document = queryData.documents?.find((d) => d.id === documentId) || null

    const rs: CoReservationFragment[] =
      document?.bills?.reduce((p: CoReservationFragment[], b) => {
        const brs: CoReservationFragment[] = (b?.reservation || []).reduce((pp: CoReservationFragment[], r) => {
          if (r) {
            return [...pp, r]
          } else {
            return pp
          }
        }, [])

        return [...p, ...brs]
      }, []) || []

    const reservations = R.uniqBy((r) => r.id, rs)

    return { document, reservations }
  }, [queryData])

  return { data, loading, error }
}

//#endregion QUERY

//#region UPSERT
export type CreateCoDocumentInput = {
  cashDeskId: string
  customerId: string
  fiscalType: FiscalType
  payments: DocumentPaymentDetailInput[]
  draft: boolean
  finalized: false
  amount: number
  deposits: DepositCheckoutInput[]
  billCheckout: BillCheckoutInput[]
  scelNote?: string
  note?: string
  groupBy: CheckoutDocumentGroupType
  createReceiptZero?: boolean
}

export type UpdateCoDocumentInput = {
  cashDeskId: string
  customerId: string
  fiscalType: FiscalType
  payments: DocumentPaymentDetailInput[]
  draft: boolean
  finalized: boolean
  documentId: string
  documentType: DocumentType
  documentDetails?: DocumentDetailInput[]
  scelNote?: string
  note?: string | null
}

export type UpdateDocumentPaymentDetailsInput = {
  id: string
  paymentActivesIds: string[] | string
}

export type UpsertCoDocumentInput = CreateCoDocumentInput | UpdateCoDocumentInput

export type UpsertCoDocumentResult =
  | {
      type: 'SUCCESS'
      documentId: string
      closedBillsIds?: string[]
    }
  | {
      type: 'FAIL'
      error?: any
    }

export function useUpsertDocument(propertyId: string, refetchQueries?: string[]) {
  const [createM, { loading: createLoading }] = useCreateCoDocumentMutation({ refetchQueries })
  const [updateM, { loading: updateLoading }] = useUpdateCoDocumentMutation({ refetchQueries })
  const [finalizeM, { loading: finalizeLoading }] = useFinalizeCoDocumentMutation({ refetchQueries })
  const [updateFinalizedM, { loading: updateFinalizedLoading }] = useUpdateFinalizedDocumentMutation({ refetchQueries })
  const [updateDocumentPaymentDetailsM, { loading: updateDocumentPaymentDetailsLoading }] = useUpdateDocumentPaymentDetailsMutation()
  const [createManualDocument, { loading: createManualDocumentLoading }] = useCreateManualDocumentMutation({ refetchQueries })

  const { id: tenantId, currency } = useTenant()
  const hotelDate = useHotelDate()

  const create: (input: CreateCoDocumentInput) => Promise<UpsertCoDocumentResult> = useCallback(
    async (input) => {
      if (input.createReceiptZero) {
        const result = await createManualDocument({
          variables: {
            input: {
              tenantId,
              propertyId,
              cashDeskId: input.cashDeskId,
              customerId: input.customerId,
              currency,
              fiscalType: input.fiscalType,
              documentPaymentDetails: [],
              scelNote: input.scelNote,
              note: input.note,
              documentDetails: [],
              draft: input.draft,
            },
          },
        })
        const documentId = result.data?.createManualDocument?.id
        if (documentId) {
          return { type: 'SUCCESS', documentId }
        } else {
          return { type: 'FAIL', error: result.errors }
        }
      }

      const result = await syncMutation(createM, {
        variables: {
          input: {
            tenantId: tenantId,
            cashDeskId: input.cashDeskId,
            customerId: input.customerId,
            currency,
            fiscalType: input.fiscalType,
            payments: input.payments,
            deposits: input.deposits,
            billCheckout: input.billCheckout,
            draft: input.draft,
            scelNote: input.scelNote,
            note: input.note,
            groupBy: input.groupBy,
          },
        },
        update: (cache, { data }) => {
          data?.createCheckoutDocument.bills?.map((b) => {
            cache.updateFragment(
              {
                id: `Bill:${b.id}`,
                fragment: gql`
                  fragment CloseBill on Bill {
                    open
                  }
                `,
              },
              (data) => ({ ...data, open: false }),
            )
          })
        },
      })
      const documentId = result.result?.data?.createCheckoutDocument?.id
      if (documentId) {
        return { type: 'SUCCESS', documentId, closedBillsIds: result.result?.data?.createCheckoutDocument.bills?.map((b) => b.id) }
      } else {
        return { type: 'FAIL', error: result.apolloError || result.error }
      }
    },
    [createM, tenantId, currency],
  )

  const updateDraft: (input: UpdateCoDocumentInput) => Promise<UpsertCoDocumentResult> = useCallback(
    async (input) => {
      const i: DocumentInput = {
        cashDeskId: input.cashDeskId,
        customerId: input.customerId,
        currency,
        fiscalType: input.fiscalType,
        documentPaymentDetails: input.payments,
        documentType: input.documentType,
        propertyId: propertyId,
        tenantId: tenantId,
        documentDetails: input.documentDetails,
        scelNote: input.scelNote,
        note: input.note,
      }

      try {
        const result = await updateM({
          variables: {
            documentId: input.documentId,
            input: i,
          },
          update: (cache, { data }) => {
            data?.updateDocument.bills?.map((b) => {
              cache.updateFragment(
                {
                  id: `Bill:${b.id}`,
                  fragment: gql`
                    fragment CloseBill on Bill {
                      open
                    }
                  `,
                },
                (data) => ({ ...data, open: false }),
              )
            })
          },
        })

        const documentId = result.data?.updateDocument?.id
        if (result.errors || !documentId) {
          return { type: 'FAIL', error: result.errors }
        }
        if (input.draft) {
          return { type: 'SUCCESS', documentId, closedBillsIds: result.data?.updateDocument.bills?.map((b) => b.id) }
        }
        const finalizeResult = await syncMutation(finalizeM, {
          variables: {
            tenantId: tenantId,
            documentId,
          },
        })
        if (finalizeResult.apolloError || finalizeResult.error) {
          return { type: 'FAIL', error: finalizeResult.apolloError || finalizeResult.error }
        }
        return { type: 'SUCCESS', documentId }
      } catch (error) {
        return { type: 'FAIL', error }
      }
    },
    [updateM, finalizeM, tenantId, propertyId, hotelDate, currency],
  )

  const updateFinalized: (input: UpdateCoDocumentInput) => Promise<UpsertCoDocumentResult> = useCallback(
    async (input) => {
      const result = await syncMutation(updateFinalizedM, {
        variables: {
          documentId: input.documentId,
          input: {
            cashDeskId: input.cashDeskId,
            customerId: input.customerId,
            currency,
            fiscalType: input.fiscalType,
            documentDetails: input.documentDetails,
            documentPaymentDetails: input.payments,
            documentType: input.documentType,
            note: input.note,
            propertyId: propertyId,
            tenantId: tenantId,
          },
        },
      })
      const documentId = result.result?.data?.updateFinalizedDocument.id
      if (result.apolloError || result.error || !documentId) return { type: 'FAIL', error: result.apolloError || result.error }

      return { type: 'SUCCESS', documentId }
    },
    [updateFinalizedM, tenantId, propertyId, currency],
  )

  const updatePaymentsOnly: (input: UpdateDocumentPaymentDetailsInput) => Promise<UpsertCoDocumentResult> = useCallback(
    async (input) => {
      const result = await syncMutation(updateDocumentPaymentDetailsM, {
        variables: {
          tenantId: tenantId,
          updateDocumentPaymentDetailsId: input.id,
          paymentActiveIds: input.paymentActivesIds,
        },
      })

      if (result.apolloError || result.error) return { type: 'FAIL', error: result.apolloError || result.error }

      return { type: 'SUCCESS', documentId: input.id }
    },
    [updateDocumentPaymentDetailsM, tenantId],
  )

  const upsertCoDocument: (input: UpsertCoDocumentInput | UpdateDocumentPaymentDetailsInput) => Promise<UpsertCoDocumentResult> = useCallback(
    async (input: UpsertCoDocumentInput | UpdateDocumentPaymentDetailsInput) => {
      if ('paymentActivesIds' in input) {
        return await updatePaymentsOnly(input)
      } else {
        if (`documentId` in input) {
          if (!input.finalized) return await updateDraft(input)
          return await updateFinalized(input)
        } else {
          return await create(input)
        }
      }
    },
    [create, updateDraft, updateFinalized, updatePaymentsOnly],
  )

  return { upsertCoDocument, loading: createLoading || updateLoading || finalizeLoading || updateFinalizedLoading || createManualDocumentLoading }
}
//#endregion UPSERT

//#region DELETE
export type DeleteCoDocumentResult =
  | {
      type: 'SUCCESS'
    }
  | {
      type: 'FAIL'
      error?: any
    }

export function useDeleteDraft(refetchQueries?: string[]) {
  const [deleteM] = useDeleteCoDocumentMutation({ refetchQueries })
  const { id: tenantId } = useTenant()

  const deleteDraft: (documentId: string) => Promise<DeleteCoDocumentResult> = useCallback(
    async (documentId: string) => {
      const result = await syncMutation(deleteM, {
        variables: {
          tenantId,
          documentId,
        },
        update: (cache) => {
          const cacheItem = cache.identify({ id: documentId, __typename: 'Document' })
          cache.evict({ id: cacheItem })
          cache.gc()
        },
      })
      if (result.apolloError || result.error) {
        return { type: 'FAIL', error: result.apolloError || result.error }
      } else {
        return { type: 'SUCCESS' }
      }
    },
    [deleteM, tenantId],
  )

  return deleteDraft
}
//#endregion DELETE

//#region UNDO
export type UndoCoDocumentResult =
  | {
      type: 'SUCCESS'
    }
  | {
      type: 'FAIL'
      error?: any
    }

export function useUndoDocument(refetchQueries?: string[]) {
  const [undoM] = useUndoCoDocumentMutation({ refetchQueries: refetchQueries ? [...refetchQueries, 'CoDocument'] : ['CoDocument'] })
  const { id: tenantId } = useTenant()

  const undoDocument: (documentId: string) => Promise<DeleteCoDocumentResult> = useCallback(
    async (documentId: string) => {
      const result = await syncMutation(undoM, {
        variables: {
          tenantId,
          documentId,
        },
      })
      if (result.apolloError || result.error) {
        return { type: 'FAIL', error: result.apolloError || result.error }
      } else {
        return { type: 'SUCCESS' }
      }
    },
    [undoM, tenantId],
  )

  return undoDocument
}
//#endregion UNDO

//#region CREDIT NOTE
export type CreateCreditNoteResult =
  | {
      type: 'SUCCESS'
      creditNoteId: string
      manualDocumentId?: string
    }
  | {
      type: 'PARTIAL'
      creditNoteId: string
      error?: any
    }
  | {
      type: 'FAIL'
      error?: any
    }

export function useCreateCreditNote(refetchQueries?: string[]) {
  const { id: tenantId } = useTenant()

  const [createCreditNoteM] = useCreateCreditNoteMutation({
    awaitRefetchQueries: true,
    refetchQueries: refetchQueries ? [...refetchQueries, 'CoDocument'] : ['CoDocument'],
  })
  const [createManualDocumentM] = useCreateManualDocumentMutation({
    awaitRefetchQueries: true,
    refetchQueries: refetchQueries ? [...refetchQueries, 'CoDocument'] : ['CoDocument'],
  })
  const [createAdvanceCreditNote] = useCreateCreditNoteForAdvanceMutation({
    awaitRefetchQueries: true,
    refetchQueries: refetchQueries ? [...refetchQueries, 'CoDocument'] : ['CoDocument'],
  })

  const createCreditNote: (documentId: string, documentType: DocumentType, manualDocument?: ManualDocumentInput) => Promise<CreateCreditNoteResult> = useCallback(
    async (documentId: string, documentType: DocumentType, manualDocument?: ManualDocumentInput) => {
      let cnResult: { apolloError: ApolloError | undefined; error: any; value: string | undefined } | undefined = undefined

      if (documentType === DocumentType.ADVANCE) {
        const result = await syncMutation(createAdvanceCreditNote, {
          variables: {
            input: {
              documentId: documentId,
              tenantId: tenantId,
            },
          },
        })
        cnResult = {
          apolloError: result.apolloError,
          error: result.error,
          value: result.result?.data?.createCreditNoteForAdvance,
        }
      } else {
        const result = await syncMutation(createCreditNoteM, {
          variables: {
            tenantId,
            documentId,
            reverseProduction: !manualDocument,
          },
        })
        cnResult = {
          apolloError: result.apolloError,
          error: result.error,
          value: result.result?.data?.createCreditNoteDocument,
        }
      }

      const creditNoteId = cnResult?.value
      if (cnResult?.apolloError || cnResult?.error || !creditNoteId) {
        return { type: 'FAIL', error: cnResult.apolloError || cnResult.error }
      }

      if (manualDocument) {
        const mdResult = await syncMutation(createManualDocumentM, {
          variables: {
            input: manualDocument,
          },
        })
        const manualDocumentId = mdResult.result?.data?.createManualDocument?.id
        if (mdResult.apolloError || mdResult.error || !manualDocumentId) {
          return { type: 'PARTIAL', creditNoteId, error: mdResult.apolloError || mdResult.error }
        }
        return { type: 'SUCCESS', creditNoteId, manualDocumentId }
      } else {
        return { type: 'SUCCESS', creditNoteId }
      }
    },
    [createCreditNoteM, tenantId],
  )

  return createCreditNote
}
//#endregion CREDIT NOTE
